import { AgentAvatar } from "@/components/molecules/agent-avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip } from "@/components/ui/tooltip";
import type { Agent } from "@/features/agents/types";
import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import {
  SessionsAPI,
  type SimulatedContact,
} from "@/features/sessions/api/sessions";
import { useTranslation } from "@/features/utils/i18n";
import {
  ArrowClockwiseIcon,
  ChatsCircleIcon,
  IdentificationCardIcon,
} from "@phosphor-icons/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ConversationView } from "../../calls/components/conversation-view";

type TestedAgent = Pick<Agent, "id" | "name" | "role" | "avatar_id">;

/**
 * « Tester <agent> »: the button, and the panel it opens.
 *
 * The panel is the back-office's own conversation pane — the shared `chat/`
 * message list, composer and voice bar — on a web session opened for *this*
 * agent (`POST /sessions/test`). So a member tries the agent exactly as a
 * visitor of their site would reach it, by typing or with the phone button,
 * without a web connector and whatever that connector's « Appel audio depuis le
 * chat » says: the back-office audio path mints its grant against the session,
 * not the connector.
 */
export function AgentTestButton({
  agent,
  variant,
  className,
  label,
}: {
  agent: TestedAgent;
  variant?: "default" | "outline" | "link";
  className?: string;
  /** Replaces « Tester <agent> » where the sentence around it names the agent already. */
  label?: string;
}) {
  const { t } = useTranslation("agents");
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        type="button"
        variant={variant}
        className={className}
        onClick={() => setOpen(true)}
      >
        <ChatsCircleIcon className="h-4 w-4" weight="fill" />
        {label || t("test.button", { name: agent.name })}
      </Button>
      <AgentTestSheet agent={agent} open={open} onOpenChange={setOpen} />
    </>
  );
}

/**
 * The conversation survives closing the panel — reopening it resumes the same
 * one — and « Nouvelle conversation » starts over. Each start is a real session
 * row, so opening the panel twice must not create two.
 */
export function AgentTestSheet({
  agent,
  open,
  onOpenChange,
}: {
  agent: TestedAgent;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation("agents");
  const clientId = useCurrentClient()?.client?.id || "";
  const [sessionId, setSessionId] = useState<string>();
  // Who the conversation pretends to come from (« Simuler un contact »).
  // Changing it starts a new conversation: a session's contact is resolved once,
  // when it is created.
  const [contact, setContact] = useState<SimulatedContact>({});
  // Single flight: StrictMode runs the effect below twice, and a second start
  // would be a second conversation.
  const starting = useRef(false);

  const start = useCallback(async () => {
    if (starting.current || !clientId) return;
    starting.current = true;
    try {
      const session = await SessionsAPI.startAgentTest(
        clientId,
        agent.id,
        contact,
      );
      setSessionId(session.id);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("test.startError"),
      );
      onOpenChange(false);
    } finally {
      starting.current = false;
    }
  }, [clientId, agent.id, contact, onOpenChange, t]);

  useEffect(() => {
    if (open && !sessionId) void start();
  }, [open, sessionId, start]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full gap-0 p-0 sm:max-w-md">
        <SheetHeader className="border-b pr-12">
          <div className="flex items-center gap-3">
            <AgentAvatar
              avatarId={agent.avatar_id}
              name={agent.name}
              size="lg"
            />
            <div className="min-w-0 flex-1">
              <SheetTitle className="truncate">
                {t("test.title", { name: agent.name })}
              </SheetTitle>
              {agent.role && (
                <div className="truncate text-xs text-muted-foreground">
                  {agent.role}
                </div>
              )}
            </div>
            <SimulateContactButton
              contact={contact}
              disabled={!sessionId}
              onChange={(next) => {
                setContact(next);
                setSessionId(undefined);
              }}
            />
            <Tooltip content={t("test.restart")}>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="shrink-0"
                aria-label={t("test.restart")}
                disabled={!sessionId}
                onClick={() => setSessionId(undefined)}
              >
                <ArrowClockwiseIcon className="h-4 w-4" />
              </Button>
            </Tooltip>
          </div>
          <SheetDescription className="text-xs">
            {t("test.description")}
          </SheetDescription>
          {(contact.phone_number || contact.email) && (
            <div className="truncate text-xs font-medium">
              {t("test.simulate.active", {
                contact: [contact.phone_number, contact.email]
                  .filter(Boolean)
                  .join(" · "),
              })}
            </div>
          )}
        </SheetHeader>

        {sessionId ? (
          <ConversationView
            key={sessionId}
            sessionId={sessionId}
            hideHeader
            composer="chat"
          />
        ) : (
          <div className="space-y-4 p-6">
            <Skeleton className="h-16 w-2/3" />
            <Skeleton className="ml-auto h-10 w-1/2" />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

/**
 * « Simuler un contact »: the conversation as if it came from this phone number
 * and/or e-mail, so the agent meets the customer behind them — their record,
 * their appointments, their past conversations. Applying it starts a new
 * conversation; clearing both fields goes back to an anonymous visitor.
 */
function SimulateContactButton({
  contact,
  disabled,
  onChange,
}: {
  contact: SimulatedContact;
  disabled?: boolean;
  onChange: (contact: SimulatedContact) => void;
}) {
  const { t } = useTranslation("agents");
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState(contact.phone_number || "");
  const [email, setEmail] = useState(contact.email || "");
  const active = !!(contact.phone_number || contact.email);

  const apply = (next: SimulatedContact) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        // Reopening shows what is applied, not an abandoned draft.
        if (next) {
          setPhone(contact.phone_number || "");
          setEmail(contact.email || "");
        }
        setOpen(next);
      }}
    >
      <Tooltip content={t("test.simulate.button")}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant={active ? "secondary" : "ghost"}
            size="icon"
            className="shrink-0"
            aria-label={t("test.simulate.button")}
            disabled={disabled}
          >
            <IdentificationCardIcon
              className="h-4 w-4"
              weight={active ? "fill" : "regular"}
            />
          </Button>
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent align="end" className="w-80">
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            apply({
              phone_number: phone.trim() || undefined,
              email: email.trim() || undefined,
            });
          }}
        >
          <div className="space-y-1">
            <div className="text-sm font-medium">
              {t("test.simulate.title")}
            </div>
            <p className="text-xs text-muted-foreground">
              {t("test.simulate.description")}
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="agent-test-phone">
              {t("test.simulate.phone")}
            </Label>
            <Input
              id="agent-test-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+33 6 12 34 56 78"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="agent-test-email">
              {t("test.simulate.email")}
            </Label>
            <Input
              id="agent-test-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="marie@exemple.fr"
            />
          </div>
          <div className="flex justify-end gap-2">
            {active && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => apply({})}
              >
                {t("test.simulate.clear")}
              </Button>
            )}
            <Button type="submit" size="sm" disabled={!phone.trim() && !email.trim()}>
              {t("test.simulate.apply")}
            </Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
}
